import React, { useContext } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { AuthContext, type AuthUser } from "../AuthContext";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { confirmSignIn } from "../AuthApi";
import {
  TextButton,
  TextInput,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import useLogger from "../../Logger/useLogger";

interface SignInMFAFormValues {
  totpToken: string;
}

const SignInMFA: React.FC = () => {
  const {
    authState: { user },
    populateAuthentication,
  } = useContext(AuthContext);
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    setError,
    reset,
  } = useForm<SignInMFAFormValues>();
  const history = useHistory();
  const { logError } = useLogger();

  const onSubmit = (data: SignInMFAFormValues) =>
    // AuthApi.confirmSignIn types `user` as the aws-amplify CognitoUser and
    // `mfaType` as a narrow challenge-name union; auth state holds the augmented
    // AuthUser whose challengeName is a free-form string, so cast at the call site.
    confirmSignIn(
      user as unknown as CognitoUser,
      data.totpToken,
      user?.challengeName as "SMS_MFA" | "SOFTWARE_TOKEN_MFA" | null | undefined
    )
      .then((cognitoUser) => {
        const authUser = cognitoUser as unknown as AuthUser;
        return populateAuthentication(authUser, authUser?.challengeName);
      })
      .then(() => {
        reset();
        history.push("/");
      })
      .catch((err) => {
        logError(err, "Sign in MFA confirmation failed");
        setError("totpToken", {
          type: "error",
          message: t("An error occurred. Sign in confirmation failed."),
        });
      });

  return (
    <div className="login-page-body">
      <div className="login-page-title bold">{t("Confirm Sign in")}</div>
      <form className={`login-page-form`} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          // @ts-expect-error wrapperClassName is not a Controller prop; dead prop ignored at runtime, kept to preserve behavior
          wrapperClassName={`login-username`}
          name={"totpToken"}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <TextInput
                placeholder={"Enter 6 digit code"}
                type={"text"}
                isLarge
                error={errors && errors.totpToken ? errors.totpToken : null}
                value={value}
                onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                  onChange(val)
                }
                onBlur={onBlur}
                applyAutoFocus
                disabled={isSubmitting}
              />
            );
          }}
          control={control}
          rules={{ required: REQUIRED_ERROR_MESSAGE }}
        />
        <TextButton
          type="submit"
          label={!isSubmitting ? "Confirm" : ""}
          rightIcon={isSubmitting ? "seem-icon-spinner spinner" : ""}
          buttonStyle={"primary"}
          disabled={isSubmitting}
          className="login-btn"
          size={"xl"}
          isFullWidth
          isSquare
        />
      </form>
    </div>
  );
};

export default SignInMFA;
